import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { QRCodeSVG } from "qrcode.react";
import PDFFooter from "../../../../components/UI/PDFFooter";
import PDFHeader from "../../../../components/UI/PDFHeader";
import SalesReturnPDFInfo from "./Info";
import { getSalesReturnById } from "../../../../features/Sales/SalesReturn/sales-return-actions";
import { salesReturnActions } from "../../../../features/Sales/SalesReturn/sales-return-slice";
import ContactService from "../../../../services/Contact";
import { useAuth } from "../../../../hooks/use-auth";
import SalesPdf from "../../../../components/Table/Pdf/SalesPdf";
import CustomizedScrollbar from "../../../../components/Custom/CustomizedScrollbar";
import { unixToDateWithFormat } from "../../../../utils/date-converter";
import EmployeeNameComponent from "../../../../components/UI/EmployeeNameComponent";
import DateWithUnderline from "../../../../components/UI/DateWithUnderline";

const creditNoteSignerLabels = [
  "ผู้ส่งสินค้า (ลูกค้า)",
  "ผู้รับสินค้า (คลัง)",
  "ผู้อนุมัติ",
];

// Sales Return signers: title shown on top, position (bold) under the line.
// Steps 1-6 map to the approval workflow; shared by both e-sig and plain prints.
const SR_SIGNERS = [
  { step: 1, title: "รับทราบสินค้าที่คืนจากลูกค้า", position: "ผจก.ฝ่ายขาย" },
  {
    step: 2,
    title: "รับทราบและนำสินค้ากลับมาจากลูกค้า",
    position: "หัวหน้าแผนกจัดส่ง",
  },
  { step: 3, title: "ตรวจสอบสินค้า", position: "ฝ่ายตรวจสอบ/QC" },
  { step: 4, title: "นำเข้าคลัง กรณีสินค้าใช้ได้", position: "สโตร์" },
  {
    step: 5,
    title: "กรณีสินค้าไม่ได้มาตรฐานและนำไปหลอม",
    position: "ผจก./กรรมการผู้จัดการ",
  },
  { step: 6, title: "ตรวจสอบ", position: "แผนกบัญชี" },
];

const SR_SIG_CELL_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  breakInside: "avoid",
  mt: 1,
};

// Approval date rendered as underlined dd / mm / yyyy segments (blank segments
// when there is no date, e.g. plain prints or unsigned steps).
const SignatureDate = ({ unix }) => {
  const [dd, mm, yyyy] = unix
    ? unixToDateWithFormat(unix).split("/")
    : ["", "", ""];
  const segSx = {
    fontSize: 12,
    borderBottom: "1px solid #000",
    textAlign: "center",
    lineHeight: 1.4,
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: 0.5,
        mt: 0.5,
      }}
    >
      <Typography component="span" sx={{ ...segSx, minWidth: "24px" }}>
        {dd || " "}
      </Typography>
      <Typography component="span" sx={{ fontSize: 12 }}>
        /
      </Typography>
      <Typography component="span" sx={{ ...segSx, minWidth: "24px" }}>
        {mm || " "}
      </Typography>
      <Typography component="span" sx={{ fontSize: 12 }}>
        /
      </Typography>
      <Typography component="span" sx={{ ...segSx, minWidth: "40px" }}>
        {yyyy || " "}
      </Typography>
    </Box>
  );
};

const InlineSignatureGrid = ({ labels }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      gap: "1.5rem",
      mt: 4,
      flexWrap: "nowrap",
    }}
  >
    {labels.map((label, index) => (
      <Box
        key={index}
        sx={{
          width: 150,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          breakInside: "avoid",
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
          _________________
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
          _____/_____/_____
        </Typography>
        <Typography sx={{ fontSize: 12 }}>{label}</Typography>
      </Box>
    ))}
  </Box>
);

// Credit Note e-signature: normal e-sig layout (role label on top, signature,
// approver name + position, then the approval date) matching the shared
// PDFSignerESignature style — intentionally different from the Sales Return
// layout. ผู้รับสินค้า (คลัง) -> Approver step 4, ผู้อนุมัติ -> Approver step 5,
// snapshotted in employee_activity.approved_data (taken at approval time).
const CreditNoteESignatureGrid = ({ data }) => {
  const approvedData = data?.employee_activity?.approved_data || [];
  const byStep = (step) =>
    approvedData.find(
      (a) => a.step_number === step && a.approval_status === "APPROVED"
    );
  const cells = [
    { label: "ผู้ส่งสินค้า (ลูกค้า)", entry: null, blank: true },
    { label: "ผู้รับสินค้า (คลัง)", entry: byStep(4) },
    { label: "ผู้อนุมัติ", entry: byStep(5) },
  ];
  return (
    <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      gap: "1.5rem",
      mt: 4,
      flexWrap: "nowrap",
    }}
  >
      {cells.map((cell, index) => {
        const emp = cell.blank ? null : cell.entry?.approved_by;
        const hasSig = emp?.has_e_signature && emp?.e_signature_img_url;
        return (
          <Box
            key={index}
            sx={{
              width: 150,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              breakInside: "avoid",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {cell.label}
            </Typography>
            {hasSig ? (
              <img
                alt="e-signature"
                src={emp.e_signature_img_url}
                style={{
                  width: 90,
                  height: 45,
                  objectFit: "contain",
                  marginTop: 8,
                }}
              />
            ) : (
              <Box sx={{ height: "45px", mt: 1 }} />
            )}
            <EmployeeNameComponent employee={emp} />
            <DateWithUnderline
              created_date={cell.blank ? null : cell.entry?.created_date}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// Sales Return signature block, shared by e-sig and plain prints.
// Per cell: title on top, signature (e-sig image or blank line), date, then
// the position in bold. The QR code and the customer signature occupy the
// first column of each row. e-sig stamps approver signatures and the approval
// date by step from employee_activity.approved_data.
const SalesReturnSignatureGrid = ({ data, isESig }) => {
  const approvedData = data?.employee_activity?.approved_data || [];
  const byStep = (step) =>
    approvedData.find(
      (a) => a.step_number === step && a.approval_status === "APPROVED"
    );

  const renderSigner = (signer) => {
    const entry = byStep(signer.step);
    const emp = entry?.approved_by;
    const hasSig = isESig && emp?.has_e_signature && emp?.e_signature_img_url;
    return (
      <Grid item xs={3} key={signer.step} sx={SR_SIG_CELL_SX}>
        <Typography sx={{ fontSize: 11, mb: 0.5 }}>{signer.title}</Typography>
        {hasSig ? (
          <img
            alt="e-signature"
            src={emp.e_signature_img_url}
            style={{ width: 80, height: 36, objectFit: "contain" }}
          />
        ) : (
          <Box sx={{ height: "36px" }} />
        )}
        <Box
          sx={{
            width: "90%",
            borderBottom: "1px solid #000",
            mt: 0.5,
            mb: 0.75,
          }}
        />
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          {signer.position}
        </Typography>
        <SignatureDate unix={isESig ? entry?.created_date : null} />
      </Grid>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={3} sx={SR_SIG_CELL_SX}>
        <QRCodeSVG value={window.location.href} size={64} />
        <Typography sx={{ fontSize: 12, mt: 0.5 }}>สแกนดูเอกสาร</Typography>
      </Grid>
      {SR_SIGNERS.slice(0, 3).map(renderSigner)}
      <Grid item xs={3} sx={SR_SIG_CELL_SX}>
        <Typography sx={{ fontSize: 11, mb: 0.5 }}>ลูกค้า</Typography>
        <Box sx={{ height: "36px" }} />
        <Box
          sx={{
            width: "90%",
            borderBottom: "1px solid #000",
            mt: 0.5,
            mb: 0.75,
          }}
        />
        <SignatureDate unix={null} />
      </Grid>
      {SR_SIGNERS.slice(3, 6).map(renderSigner)}
    </Grid>
  );
};

const SalesReturnPDF = () => {
  const dispatch = useDispatch();
  const { salesReturn } = useSelector((state) => state.salesReturn);
  const [contact, setContact] = useState({
    contact_name: "",
    identity_no: "",
  });
  const [searchParams] = useSearchParams();
  const docType =
    searchParams.get("doc") === "credit_note" ? "credit_note" : "sales_return";
  const type = searchParams.get("type");
  const isCreditNote = docType === "credit_note";
  const titleTh = isCreditNote
    ? "ใบลดหนี้/Credit Note"
    : "ใบรับคืน/Sales return";
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: salesReturn?.document_id,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { user } = useAuth();

  useEffect(() => {
    dispatch(
      getSalesReturnById(
        {
          document_id: id,
        },
        user,
        enqueueSnackbar
      )
    );
    return () => {
      dispatch(salesReturnActions.resetSalesReturn());
    };
  }, [dispatch, enqueueSnackbar, id, user]);

  useEffect(() => {
    const getContact = async () => {
      if (salesReturn && salesReturn.contact_document_id) {
        const contactData = await ContactService.getContact({
          document_id: salesReturn.contact_document_id,
        });
        setContact({
          contact_name:
            contactData.contact_name_1 +
            (contactData.contact_name_2
              ? " " + contactData.contact_name_2
              : ""),
          identity_no: contactData.identity_no,
        });
      }
    };
    getContact();
  }, [salesReturn]);

  if (!salesReturn) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h5">{titleTh}</Typography>
          <Typography>เลขที่เอกสาร {id ? id : "-"}</Typography>
        </Box>
        <Box>
          <Button size="small" variant="outlined" onClick={handlePrint}>
            พิมพ์เอกสาร
          </Button>
        </Box>
      </Box>

      <Box sx={{ m: "10px auto", width: "fit-content" }}>
        <Box
          sx={{
            width: "210mm",
            height: "297mm",
            border: "1px solid #eee",
            borderRadius: "5px",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <CustomizedScrollbar
            sx={{
              height: "100%",
            }}
          >
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 3,
              }}
              ref={componentRef}
            >
              <Box>
                <PDFHeader documentType={titleTh} />
                <Divider />
                <SalesReturnPDFInfo
                  salesReturn={salesReturn}
                  contact={contact}
                  docType={docType}
                />
                <Divider />
                <SalesPdf itemList={salesReturn.item_list} />
                <PDFFooter
                  data={salesReturn}
                  isSalesReturn
                  isCreditNote={isCreditNote}
                />
              </Box>
              <Box
                sx={{
                  py: 2,
                  breakInside: "avoid",
                }}
              >
                {isCreditNote ? (
                  type === "e-sig" ? (
                    <CreditNoteESignatureGrid data={salesReturn} />
                  ) : (
                    <InlineSignatureGrid labels={creditNoteSignerLabels} />
                  )
                ) : (
                  <SalesReturnSignatureGrid
                    data={salesReturn}
                    isESig={type === "e-sig"}
                  />
                )}
              </Box>
            </Box>
          </CustomizedScrollbar>
        </Box>
      </Box>
    </Box>
  );
};

export default SalesReturnPDF;
